// Updated QuizSession05.js component with dashboard aesthetic matching Session 2
import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { updateSessionProgress } from "../../supabase";

// SVG Check Mark Component - Updated to match dashboard
const CheckMarkSVG = ({ size = 16, color = "#374151" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke={color} 
    strokeWidth="3" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <polyline points="20,6 9,17 4,12"></polyline>
  </svg>
);

const QuizSession05 = ({ 
  showAnswers, 
  reviewMode = false, 
  onComplete, 
  resetQuiz, 
  requireCorrectAnswer = false,
  // New props for post-assessment
  postAssessmentClicked = false,
  postAssessmentCompleted = false,
  onPostAssessmentClick,
  onPostAssessmentComplete,
  postAssessmentUrl,
  currentSessionNumber = 5
}) => {
  const { user } = useAuth();
  const [score, setScore] = useState(0);
  const [timeTaken, setTimeTaken] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  
  // New state for post-assessment flow
  const [showPostAssessment, setShowPostAssessment] = useState(false);

  useEffect(() => {
    if (resetQuiz) {
      console.log("🔄 Resetting quiz state");
      setQuizCompleted(false);
      setUserAnswers([]);
      setCurrentQuestionIndex(0);
      setStartTime(Date.now());
      setIsFinished(false);
      setScore(0);
      setTimeTaken(0);
      setFeedback("");
      setSaveError(null);
      setShowPostAssessment(false);
    }
  }, [resetQuiz]);

  const questions = [
    {
      question: "What is 'mindfulness of pain and discomfort' in the Face-Forward-Web program?",
      options: [
        "Ignoring pain completely and pretending it doesn't exist.",
        "Analyzing pain to find its exact medical cause.",
        "Observing pain and unpleasant sensations with curiosity and without judgment.",
        "Fighting against pain with maximum mental effort."
      ],
      correctAnswer: "Observing pain and unpleasant sensations with curiosity and without judgment.",
      explanation: "Mindfulness of pain involves paying attention to pain with curiosity rather than fear or judgment, which can help reduce the emotional suffering that often accompanies physical discomfort.",
      explanations: {
        "Ignoring pain completely and pretending it doesn't exist.": "Try again. Mindfulness involves awareness, not ignoring pain.",
        "Analyzing pain to find its exact medical cause.": "Try again. Mindfulness focuses on present-moment awareness, not analytical problem-solving.",
        "Observing pain and unpleasant sensations with curiosity and without judgment.": "Correct! This approach helps reduce emotional reactivity to pain.",
        "Fighting against pain with maximum mental effort.": "Try again. Fighting pain often increases suffering - mindfulness teaches a gentler approach."
      }
    },
    {
      question: "When choosing which Face-Forward-Web skills to use in different situations, what should guide your decision?",
      options: [
        "Always use the same skill regardless of the situation.",
        "Choose skills based on what has been most helpful for your specific needs and circumstances.",
        "Only use the most recent skill you learned.",
        "Use whichever skill takes the least amount of time."
      ],
      correctAnswer: "Choose skills based on what has been most helpful for your specific needs and circumstances.",
      explanation: "Different situations may call for different skills. For example, deep breathing might be best for acute stress, while acceptance might be more helpful when dealing with unpredictable pain flares.",
      explanations: {
        "Always use the same skill regardless of the situation.": "Try again. Different situations benefit from different approaches - flexibility is key.",
        "Choose skills based on what has been most helpful for your specific needs and circumstances.": "Correct! Personalizing your approach based on what works best for you is most effective.",
        "Only use the most recent skill you learned.": "Try again. All the skills you've learned can be valuable in different situations.",
        "Use whichever skill takes the least amount of time.": "Try again. Effectiveness, not speed, should guide your choice of skills."
      }
    },
    {
      question: "What is the main goal of completing the Face-Forward-Web program?",
      options: [
        "To completely eliminate all orofacial pain symptoms.",
        "To develop a personalized toolkit of skills for managing pain and living meaningfully despite chronic pain.",
        "To prove that pain is not real.",
        "To become completely dependent on these techniques for daily functioning."
      ],
      correctAnswer: "To develop a personalized toolkit of skills for managing pain and living meaningfully despite chronic pain.",
      explanation: "The program aims to help people build a comprehensive set of skills they can use to manage pain more effectively and continue engaging in meaningful life activities.",
      explanations: {
        "To completely eliminate all orofacial pain symptoms.": "Try again. While pain may improve, the primary goal is better management and coping, not complete elimination.",
        "To develop a personalized toolkit of skills for managing pain and living meaningfully despite chronic pain.": "Correct! The program provides tools for better pain management and meaningful living.",
        "To prove that pain is not real.": "Try again. The program validates that pain is real while teaching better ways to cope with it.",
        "To become completely dependent on these techniques for daily functioning.": "Try again. The goal is to build independence and confidence in managing pain."
      }
    },
    {
      question: "How can you maintain progress after completing the Face-Forward-Web program?",
      options: [
        "Stop practicing all the skills since the program is over.",
        "Only use the skills when pain is at its worst.",
        "Continue practicing the skills regularly and return to the program materials when needed.",
        "Rely entirely on family members to remind you to use the skills."
      ],
      correctAnswer: "Continue practicing the skills regularly and return to the program materials when needed.",
      explanation: "Like any skill, the techniques learned in Face-Forward-Web need ongoing practice to remain effective. Regular use helps maintain benefits and build resilience.",
      explanations: {
        "Stop practicing all the skills since the program is over.": "Try again. Continued practice is essential for maintaining benefits.",
        "Only use the skills when pain is at its worst.": "Try again. Regular practice during both good and difficult times helps maintain proficiency.",
        "Continue practicing the skills regularly and return to the program materials when needed.": "Correct! Ongoing practice and occasional review help maintain progress.",
        "Rely entirely on family members to remind you to use the skills.": "Try again. Building your own self-management skills is more empowering and sustainable."
      }
    },
    {
      question: "What should you prioritize as you continue your journey with chronic orofacial pain?",
      options: [
        "Avoiding all activities that might trigger pain.",
        "Focusing only on pain management and nothing else.",
        "Activities that give you meaning, joy, and connection with others.",
        "Comparing your progress to others with similar conditions."
      ],
      correctAnswer: "Activities that give you meaning, joy, and connection with others.",
      explanation: "Research shows that staying connected to meaningful activities and relationships is crucial for well-being, even when living with chronic pain.",
      explanations: {
        "Avoiding all activities that might trigger pain.": "Try again. Avoidance often leads to increased disability and decreased quality of life.",
        "Focusing only on pain management and nothing else.": "Try again. While pain management is important, a balanced life includes many other meaningful elements.",
        "Activities that give you meaning, joy, and connection with others.": "Correct! Meaningful activities and relationships are essential for well-being and resilience.",
        "Comparing your progress to others with similar conditions.": "Try again. Everyone's journey is different - focus on your own progress and goals."
      }
    }
  ];

  const handleFinishQuiz = async () => {
    // In review mode, just call onComplete to exit
    if (reviewMode) {
      if (onComplete) {
        onComplete();
      }
      return;
    }

    console.log('🔍 Starting quiz submission for Session 5...');
    
    if (!user) {
      console.error("❌ No user found for quiz submission");
      setSaveError("You must be logged in to submit quiz results!");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    const endTime = Date.now();
    const timeTakenInSeconds = (endTime - startTime) / 1000;

    let calculatedScore = 0;
    questions.forEach((question, index) => {
      if (userAnswers[index] === question.correctAnswer) {
        calculatedScore += 1;
      }
    });

    setScore(calculatedScore);
    setTimeTaken(Math.floor(timeTakenInSeconds));
    setIsFinished(true);
    setQuizCompleted(true);

    const quizData = {
      score: calculatedScore,
      answers: userAnswers,
      attempts: 1,
      totalQuestions: questions.length,
      timeTaken: Math.floor(timeTakenInSeconds)
    };

    const progressData = {
      quiz_completed: true,
      quiz_score: calculatedScore,
      session_completed: true,
      session_fully_completed: true,
      updated_at: new Date().toISOString()
    };

    console.log('🔍 Progress data to save:', progressData);
    console.log('🔍 Quiz data for callback:', quizData);

    try {
      console.log('🔍 Attempting to save quiz results for Session 5...');
      const result = await updateSessionProgress(user.id, currentSessionNumber, progressData);
      
      if (result) {
        console.log('✅ Quiz results saved successfully');
        
        try {
          await updateSessionProgress(user.id, currentSessionNumber, {
            quiz_answers: userAnswers,
            quiz_attempts: 1,
            quiz_completion_time: new Date().toISOString(),
            quiz_total_questions: questions.length,
            quiz_time_taken_seconds: Math.floor(timeTakenInSeconds),
            program_completed: true,
            program_completion_date: new Date().toISOString().split('T')[0]
          });
          console.log('✅ Program completion data saved successfully');
        } catch (extendedError) {
          console.warn('⚠️ Extended quiz data save failed, but core data saved:', extendedError);
        }
        
        if (onComplete) {
          console.log('✅ Quiz finished for Session 5 - calling onComplete callback');
          onComplete(quizData);
        }
        
      } else {
        throw new Error('Failed to save quiz results to database');
      }
      
    } catch (error) {
      console.error("❌ Error saving quiz result:", error);
      setSaveError(`Error saving quiz result: ${error.message}`);
      
      if (onComplete) {
        console.log('✅ Quiz finished for Session 5 (despite save error) - calling onComplete callback');
        onComplete(quizData);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle post-assessment completion and call onComplete
  const handlePostAssessmentCompletion = async () => {
    console.log("Post-assessment completed - calling onComplete");
    
    if (onPostAssessmentComplete) {
      await onPostAssessmentComplete();
    }
    
    // Now call the original onComplete callback
    if (onComplete) {
      const quizData = {
        score: score,
        answers: userAnswers,
        attempts: 1,
        totalQuestions: questions.length,
        timeTaken: timeTaken
      };
      onComplete(quizData);
    }
  };

  const handleAnswerSelection = (answer) => {
    const currentQuestion = questions[currentQuestionIndex];
    const updatedAnswers = [...userAnswers];
    updatedAnswers[currentQuestionIndex] = answer;
    setUserAnswers(updatedAnswers);
    
    // Show feedback based on the selected answer
    setFeedback(currentQuestion.explanations[answer]);
  };

  const handleNextQuestion = () => {
    // In requireCorrectAnswer mode, check if current answer is correct
    if (requireCorrectAnswer && !reviewMode) {
      const currentQuestion = questions[currentQuestionIndex];
      const selectedAnswer = userAnswers[currentQuestionIndex];
      
      if (!selectedAnswer || selectedAnswer !== currentQuestion.correctAnswer) {
        return; // Don't proceed if no answer selected or answer is incorrect
      }
    }

    setFeedback("");
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    setFeedback("");
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prevIndex) => prevIndex - 1);
    }
  };

  // Get current question data
  const currentQuestion = questions[currentQuestionIndex];
  const selectedAnswer = userAnswers[currentQuestionIndex];
  const isCorrectAnswer = selectedAnswer === currentQuestion.correctAnswer;

  // Determine if Next/Finish button should be enabled
  const canProceed = reviewMode || // Always can proceed in review mode
                    !requireCorrectAnswer || // Can proceed if not requiring correct answers
                    (selectedAnswer && isCorrectAnswer); // Can proceed if correct answer selected

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {!isFinished || reviewMode ? (
        <div>
          {/* Question Header - Updated styling to match dashboard */}
          <div style={{
            marginBottom: '20px'
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px',
              backgroundColor: '#F8F9FA',
              padding: '4px 8px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              display: 'inline-block'
            }}>
              Final Question {currentQuestionIndex + 1} of {questions.length}
            </div>

            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#374151',
              margin: '0 0 20px 0',
              lineHeight: '1.4',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
              {currentQuestion.question}
            </h3>
          </div>

          {/* Answer Options - Dashboard colors only, no red */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginBottom: '24px'
          }}>
            {currentQuestion.options.map((option, index) => {
              const isSelected = selectedAnswer === option;
              const isCorrect = option === currentQuestion.correctAnswer;
              
              let buttonColor = '#374151';
              let backgroundColor = '#FFFFFF';
              let borderColor = '#E5E7EB';
              let showCheckmark = false;

              if (reviewMode) {
                // Review mode: only highlight correct answers in green
                if (isCorrect) {
                  backgroundColor = '#ECFDF5';
                  borderColor = '#10B981';
                  buttonColor = '#374151';
                  showCheckmark = true;
                } else {
                  backgroundColor = '#FFFFFF';
                  borderColor = '#E5E7EB';
                  buttonColor = '#374151';
                }
              } else {
                // Normal mode: show selection and correct feedback
                if (isSelected) {
                  if (feedback) {
                    // Show feedback colors only for the selected answer
                    if (isCorrect) {
                      backgroundColor = '#ECFDF5';
                      borderColor = '#10B981';
                      buttonColor = '#374151';
                      showCheckmark = true;
                    } else {
                      // Incorrect answers get neutral gray styling - no red
                      backgroundColor = '#F8F9FA';
                      borderColor = '#D1D5DB';
                      buttonColor = '#374151';
                    }
                  } else {
                    // Just show selection in blue
                    backgroundColor = '#EFF6FF';
                    borderColor = '#3B82F6';
                    buttonColor = '#1B365D';
                  }
                } else {
                  // Default state for all unselected answers
                  backgroundColor = '#FFFFFF';
                  borderColor = '#E5E7EB';
                  buttonColor = '#374151';
                }
              }

              return (
                <button
                  key={index}
                  onClick={() => !reviewMode && handleAnswerSelection(option)}
                  disabled={reviewMode}
                  onMouseDown={(e) => e.preventDefault()}
                  onSelectStart={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                  style={{
                    padding: '16px 20px',
                    textAlign: 'left',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    lineHeight: '1.4',
                    minHeight: '60px',
                    color: buttonColor,
                    backgroundColor: backgroundColor,
                    border: `2px solid ${borderColor}`,
                    cursor: reviewMode ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!reviewMode && !isSelected) {
                      e.target.style.backgroundColor = '#F8F9FA';
                      e.target.style.borderColor = '#D1D5DB';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!reviewMode && !isSelected) {
                      e.target.style.backgroundColor = '#FFFFFF';
                      e.target.style.borderColor = '#E5E7EB';
                    }
                  }}
                >
                  {showCheckmark && (
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#10B981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <CheckMarkSVG size={10} color="#FFFFFF" />
                    </div>
                  )}
                  <span 
                    style={{ 
                      flex: 1,
                      pointerEvents: 'none',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none'
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onSelectStart={(e) => e.preventDefault()}
                  >
                    {option}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Show explanation in review mode or after feedback */}
          {reviewMode && currentQuestion.explanation && (
            <div style={{
              padding: '16px',
              backgroundColor: '#F0F9FF',
              borderRadius: '12px',
              border: '1px solid #E0E7FF',
              marginBottom: '24px'
            }}>
              <div style={{ 
                fontWeight: '600', 
                marginBottom: '8px',
                color: '#374151',
                fontSize: '14px'
              }}>
                Why this is correct:
              </div>
              <div style={{
                fontSize: '14px',
                color: '#374151',
                lineHeight: '1.4'
              }}>
                {currentQuestion.explanation}
              </div>
            </div>
          )}

          {/* Feedback area - green for correct, light blue for incorrect */}
          {feedback && !reviewMode && (
            <div style={{
              padding: '16px',
              backgroundColor: feedback.includes("Correct") ? "#ECFDF5" : "#DBEAFE",
              borderRadius: '12px',
              border: feedback.includes("Correct") ? '1px solid #10B981' : '1px solid #60A5FA',
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#374151',
                lineHeight: '1.4',
                fontWeight: '500'
              }}>
                {feedback}
              </div>
            </div>
          )}

          {/* Navigation - Updated to match dashboard styling */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px'
          }}>
            <button
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              style={{
                backgroundColor: currentQuestionIndex === 0 ? '#F8F9FA' : '#FFFFFF',
                color: '#374151',
                border: '1px solid #E5E7EB',
                borderRadius: '24px',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                opacity: currentQuestionIndex === 0 ? 0.5 : 1,
                boxShadow: currentQuestionIndex === 0 ? 'none' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}
            >
              Previous
            </button>

            {/* Progress Dots - Updated styling */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {questions.map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: index === currentQuestionIndex ? '#1B365D' :
                      userAnswers[index] ? '#10B981' : '#E5E7EB',
                    border: '1px solid #E5E7EB',
                    transition: 'background-color 0.2s ease'
                  }}
                />
              ))}
            </div>

            {currentQuestionIndex < questions.length - 1 ? (
              <button
                onClick={handleNextQuestion}
                disabled={!canProceed}
                style={{
                  backgroundColor: canProceed ? '#1B365D' : '#F8F9FA',
                  color: canProceed ? '#FFFFFF' : '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '24px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: canProceed ? 'pointer' : 'not-allowed',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  opacity: canProceed ? 1 : 0.5,
                  boxShadow: canProceed ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)' : 'none'
                }}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleFinishQuiz}
                disabled={(!canProceed && !reviewMode) || isSaving}
                style={{
                  backgroundColor: (reviewMode || canProceed) ? '#1B365D' : '#F8F9FA',
                  color: (reviewMode || canProceed) ? '#FFFFFF' : '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '24px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: (reviewMode || canProceed) && !isSaving ? 'pointer' : 'not-allowed',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: (reviewMode || canProceed) && !isSaving ? 1 : 0.5,
                  boxShadow: (reviewMode || canProceed) && !isSaving ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)' : 'none'
                }}
              >
                {isSaving && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #D1D5DB',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                )}
                {isSaving ? 'Saving...' : reviewMode ? 'Finish Review' : 'Complete Program'}
              </button>
            )}
          </div>
        </div>
      ) : (
        /* Quiz Completion Screen with Post-Assessment Integration */
        <div>
          {!showPostAssessment ? (
            /* Initial Quiz Complete Screen - Brief transition */
            <div style={{ 
              textAlign: 'center', 
              padding: '24px 0',
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#ECFDF5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                border: '1px solid #10B981'
              }}>
                <CheckMarkSVG size={24} color="#10B981" />
              </div>
              
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>
                Final Quiz Complete!
              </h3>
              
              {saveError && (
                <div style={{
                  fontSize: '12px',
                  color: '#374151',
                  marginBottom: '12px',
                  backgroundColor: '#F8F9FA',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #D1D5DB',
                  display: 'inline-block'
                }}>
                  {saveError}
                </div>
              )}
              
              <div style={{
                fontSize: '14px',
                color: '#6B7280',
                marginBottom: '16px',
                fontWeight: '500'
              }}>
                Score: {score} / {questions.length}
              </div>
              
              <div style={{
                backgroundColor: '#F8F9FA',
                color: '#374151',
                padding: '6px 12px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid #E5E7EB'
              }}>
                <CheckMarkSVG size={12} color="#374151" />
                {saveError ? 'Continuing despite save error' : 'Results saved successfully'}
              </div>
            </div>
          ) : (
            /* Post-Assessment Integration */
            <div>
              {!postAssessmentCompleted ? (
                <div style={{
                  padding: '24px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '16px' }}>🎉</div>
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#374151',
                    margin: '0 0 8px 0',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}>
                    Outstanding Achievement!
                  </h4>
                  <p style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    margin: '0 0 8px 0',
                    fontWeight: '500'
                  }}>
                    Final Quiz Score: {score} / {questions.length}
                  </p>
                  <p style={{
                    fontSize: '13px',
                    color: '#6B7280',
                    margin: '0 0 20px 0'
                  }}>
                    Complete the final assessment to finish the program
                  </p>
                  
                  {!postAssessmentClicked ? (
                    <>
                      <p style={{
                        fontSize: '14px',
                        color: '#374151',
                        margin: '0 0 16px 0'
                      }}>
                        Please complete our final post-program assessment to officially complete Face-Forward-Web.
                      </p>
                      
                      <button
                        onClick={onPostAssessmentClick}
                        style={{
                          backgroundColor: '#1B365D',
                          color: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '24px',
                          padding: '12px 24px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        📋 Open Final Assessment
                      </button>
                    </>
                  ) : (
                    <div>
                      <div style={{
                        backgroundColor: '#ECFDF5',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '16px',
                        border: '1px solid #10B981',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}>
                        <CheckMarkSVG size={16} color="#10B981" />
                        <p style={{
                          fontSize: '13px',
                          color: '#374151',
                          margin: 0,
                          fontWeight: '500'
                        }}>
                          Assessment link opened successfully
                        </p>
                      </div>
                      
                      <p style={{
                        fontSize: '13px',
                        color: '#6B7280',
                        margin: '0 0 16px 0'
                      }}>
                        Complete the assessment in the new tab, then confirm below:
                      </p>
                      
                      <button
                        onClick={handlePostAssessmentCompletion}
                        style={{
                          backgroundColor: '#10B981',
                          color: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '24px',
                          padding: '12px 24px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          width: '100%'
                        }}
                      >
                        I've Completed the Final Assessment
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                /* Final completion screen */
                <div style={{
                  textAlign: 'center',
                  padding: '32px 0',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏆</div>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}>
                    Face-Forward-Web Complete!
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    margin: '0 0 16px 0'
                  }}>
                    Congratulations on completing the entire program. You now have a comprehensive toolkit for managing chronic orofacial pain.
                  </p>
                  
                  <div style={{
                    backgroundColor: '#ECFDF5',
                    color: '#10B981',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: '1px solid #10B981'
                  }}>
                    <CheckMarkSVG size={12} color="#10B981" />
                    Program fully completed
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          /* Remove all text selection and highlighting completely */
          *, *::before, *::after {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
            -webkit-touch-callout: none !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          
          /* Extra prevention for buttons and spans */
          button, button *, span, span *, div, div *, p, p * {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
            -webkit-touch-callout: none !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          
          /* Remove focus outlines and tap highlights */
          button:focus, button:active, div:focus, div:active {
            outline: none !important;
            -webkit-tap-highlight-color: transparent !important;
            box-shadow: none !important;
          }
          
          button::-moz-focus-inner {
            border: 0 !important;
            outline: 0 !important;
          }
          
          /* Remove all selection highlighting - multiple approaches */
          ::selection {
            background: transparent !important;
            color: inherit !important;
          }
          
          ::-moz-selection {
            background: transparent !important;
            color: inherit !important;
          }
          
          ::-webkit-selection {
            background: transparent !important;
            color: inherit !important;
          }
          
          /* Prevent weird browser highlighting on mobile and desktop */
          button:active, button:focus, span:active, span:focus {
            background-color: inherit !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          
          /* Prevent mouse selection events */
          button {
            -webkit-appearance: none !important;
            -moz-appearance: none !important;
            appearance: none !important;
            -webkit-user-drag: none !important;
            -khtml-user-drag: none !important;
            -moz-user-drag: none !important;
            -o-user-drag: none !important;
            user-drag: none !important;
          }
        `
      }} />
    </div>
  );
};

export default QuizSession05;